import { type EntityState, splitSegmentKey } from '@dl/shared';

/**
 * Pure selector for the entity-level **History / chronology** surface
 * (#247, `30-design-system.md §7.14`).
 *
 * The History tab answers "how did this entity change over the campaign" —
 * the whole-entity timeline of fact changes, as opposed to the per-*fact*
 * rollback panel (`fact-row-editor.ts:viewHistory`) which scopes to one
 * predicate chain. Both read the SAME folded op-log projection
 * (`EntityState.facts[*].versions`); this selector flattens every version
 * of every fact chain into one reverse-chronological stream.
 *
 * GM-truth note (projection chokepoint): this reads the GM-side folded
 * `EntityState` — drafts/reveals are already correct for the GM there, and
 * the projection keeps non-GM reads filtered upstream. It constructs no
 * `VisibleFact` and re-implements no reveal filter; the History tab is
 * rendered only inside the GM-view gate in `app.ts`, and is structurally
 * replaced by the reveal-filtered Preview-as panel while previewing — so
 * there is no leak path (a per-edit chronology is strictly more than the
 * single visible value the chokepoint discloses to a non-GM Viewer).
 */

/** One change in the entity's chronology — one applied version of one fact chain. */
export interface ChronologyEntry {
  /**
   * The op-log fact-map key this version folds from (`base`, `base#seg_…`,
   * or `base#op_…`). Used as the rollback target + stable list key.
   */
  predicateKey: string;
  /** The logical predicate (segment / instance suffix stripped) — drives the label. */
  basePredicate: string;
  /** The folded value at this version. */
  value: unknown;
  /** Op author label (`GM`, `llm`, a viewer kind, …) as folded onto the version. */
  author: string;
  /** Real-world write time (ISO 8601) of this version — the chronology sort key. */
  writtenAtReal: string;
  /** Position of this version in its source chain (oldest-first) — the rollback index. */
  versionIndex: number;
  /** True when this is the current chain tip (the live value for this key). */
  isLatest: boolean;
  /** Current chain status (canon / draft / …) — chain-level, repeated per version. */
  status: string;
  /** Current chain owner kind (canon / belief) — chain-level, repeated per version. */
  ownerKind: string;
  /** Whether the chain is soft-deleted (history is append-only; deleted facts still show). */
  softDeleted: boolean;
}

export interface EntityChronology {
  /** Every version of every (non-reserved) fact chain, newest-first. */
  entries: ChronologyEntry[];
  /** Distinct base predicates that ever carried a value. */
  predicateCount: number;
  /** Total number of recorded changes (versions). */
  changeCount: number;
  /** Convenience flag for the empty-state render. */
  isEmpty: boolean;
}

/** Reserved meta slots (`__pack`, `__campaign`) are never entity facts. */
function isReservedKey(key: string): boolean {
  return key.startsWith('__');
}

/**
 * Fold an `EntityState` into its whole-entity chronology. Pure +
 * deterministic: a stable sort keeps equal-timestamp ties in insertion
 * order (no `Date` parsing — ISO 8601 strings sort lexicographically and
 * the op-log writes them in HLC order). A soft-deleted entity yields an
 * empty chronology (the entity itself is hidden everywhere else).
 */
export function buildEntityChronology(entity: EntityState | undefined): EntityChronology {
  if (!entity || entity.soft_deleted) {
    return { entries: [], predicateCount: 0, changeCount: 0, isEmpty: true };
  }

  const entries: ChronologyEntry[] = [];
  const basePredicates = new Set<string>();

  for (const [key, fact] of Object.entries(entity.facts)) {
    if (isReservedKey(key)) continue;
    if (fact.versions.length === 0) continue;
    const { base } = splitSegmentKey(key);
    basePredicates.add(base);
    const tipIndex = fact.versions.length - 1;
    const ownerKind = fact.owner.kind;
    fact.versions.forEach((version, idx) => {
      entries.push({
        predicateKey: key,
        basePredicate: base,
        value: version.value,
        author: version.author,
        writtenAtReal: version.written_at_real,
        versionIndex: idx,
        isLatest: idx === tipIndex,
        status: fact.status,
        ownerKind,
        softDeleted: fact.soft_deleted,
      });
    });
  }

  // Newest-first. ISO 8601 strings are lexically comparable; a stable sort
  // (the entries array is built in deterministic key-insertion order) keeps
  // equal timestamps in a fixed order so the view never reshuffles.
  entries.sort((a, b) => (a.writtenAtReal < b.writtenAtReal ? 1 : a.writtenAtReal > b.writtenAtReal ? -1 : 0));

  return {
    entries,
    predicateCount: basePredicates.size,
    changeCount: entries.length,
    isEmpty: entries.length === 0,
  };
}
